import { RestrictedTag } from '@apis/TagManager/model';
import { ActionIcon, Divider, Group, Space, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { AnchorButton, TooltipWhite } from '@root/Design/Primitives';
import { Check, Tag, Trash, X } from 'tabler-icons-react';
import styled from '@emotion/styled';
import { IRestrictedTagStats } from '@root/Services/Resources/TagStatsService';
import { Fragment, ReactNode, useCallback } from 'react';
import { useAsync } from '@react-hookz/web';
import { UdIcon } from '@root/Components/Picker/IconPicker';
import { useFmtSvc } from '@root/Services/FormatService';
import { useLink } from '@root/Services/Router/Router';
import { useNav } from '@root/Services/NavigationService';
import { useElementSize } from '@mantine/hooks';

export const RestrictedTagsList = (props: {
    companyId: number;
    restrictedTags: RestrictedTag[];
    tagStats: IRestrictedTagStats | undefined;
    readonly: boolean;
    onClick: (actionType: string, selectedTag: RestrictedTag) => void;
    onRefresh: () => Promise<void>;
}) => {
    const { onRefresh, readonly, tagStats } = props;
    const { getDescendUrl, getMoveUrl } = useNav();
    const fmtSvc = useFmtSvc();
    const { colors } = useMantineTheme();
    const [{ status }, { execute }] = useAsync(onRefresh);

    const handleRemoveTag = (tag: RestrictedTag) => {
        props.onClick('remove', tag);
    };

    const handleEditTag = (tag: RestrictedTag) => {
        props.onClick('edit', tag);
    };

    const handleRefresh = useCallback(async () => {
        execute();
    }, [onRefresh]);

    const items = props.restrictedTags.map((t) => {
        const name = t.TagName ?? '';
        const stats = tagStats?.getStats(t.TagName ?? '');
        return {
            tag: t,
            scoreEnabled: t.ClarityScore || t.CompletenessScore || t.ComplianceScore || t.CoverageScore,
            resources: [
                {
                    value: tagStats?.getStats(name)?.resources,
                    link: getDescendUrl('tag-explorer', {
                        state: JSON.stringify({
                            columns: [],
                            filters: [{ Operation: 'isNotNull', Operands: [{ Field: `CsTags.${name}` }] }],
                            sort: [],
                        }),
                    }),
                    tooltip: 'View Resources',
                },
            ],
            policies: [...(tagStats?.platforms ?? [null])].map((p) => ({
                value: !p || !stats?.policies ? undefined : stats.policies[p],
                link: getMoveUrl('tag-policies', { tag: name, platform: p ?? '' }),
                tooltip: !p ? '' : `View ${fmtSvc.adjustCspName(p ?? '')} Policies`,
            })),
            rules: [
                {
                    value: stats?.rules,
                    link: getDescendUrl('tag-automation', { tag: name }),
                    tooltip: 'View Tag Automation Rules',
                },
            ],
        };
    });

    return (
        <ListContainer>
            <ListRow className="list-header">
                <span />
                <Text size="sm" color="dimmed">
                    Tag Key
                </Text>
                <TagStatHeader text="Scores Enabled" />
                <TagStatHeader text="Resources" />
                <TagStatHeader text="Auto-Tag Rules" />
                <TagStatHeader text="Tag Policies" />
                <Tooltip withinPortal label="Refresh">
                    <ActionIcon onClick={handleRefresh}>
                        <i className={`ti ti-refresh ${status === 'loading' ? 'ti-spin' : ''}`} />
                    </ActionIcon>
                </Tooltip>
            </ListRow>
            <Space h={8} />
            {items.map(({ tag, ...item }) => (
                <ListRow key={tag.Id} clickable>
                    <span style={{ justifySelf: 'center' }}>
                        {!tag.Icon ? <Tag size={20} stroke={colors.gray[6]} /> : <UdIcon withBackground size={20} icon={tag.Icon} />}
                    </span>
                    <AnchorButton
                        text={
                            <TooltipWhite disabled={!tag.Description} label={tag.Description ?? ''}>
                                <span>{tag.TagName}</span>
                            </TooltipWhite>
                        }
                        onClick={() => handleEditTag(tag)}
                    />
                    <Group noWrap spacing={4}>
                        <Text size="sm" color="dimmed" role="rowheader">
                            Scores Enabled
                        </Text>
                        {item.scoreEnabled ? (
                            <Check size={18} strokeWidth={4} stroke={colors.gray[6]} />
                        ) : (
                            <X size={18} strokeWidth={4} stroke={colors.gray[6]} />
                        )}
                    </Group>
                    <TagStat label="Resources" items={item.resources} />
                    <TagStat label="Auto-Tag Rules" items={item.rules} />
                    <TagStat label="Tag Policies" items={item.policies} />
                    {readonly ? (
                        <span />
                    ) : (
                        <Tooltip withinPortal label="Remove">
                            <ActionIcon mr={8} sx={{ alignSelf: 'center', justifySelf: 'center' }} onClick={() => handleRemoveTag(tag)}>
                                <Trash size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </ListRow>
            ))}
        </ListContainer>
    );
};

function ListContainer({ children }: { children: ReactNode }) {
    const { ref, width } = useElementSize();

    return (
        <ListContainerEl className={width < 700 ? '--list-narrow' : ''} ref={ref}>
            {children}
        </ListContainerEl>
    );
}

function TagStat(props: { label: string; items: Array<{ value?: number; link: string; tooltip?: string }> }) {
    const { label, items } = props;
    const fmtSvc = useFmtSvc();
    const link = useLink();

    return (
        <Group className="list-stat" align="center" noWrap spacing={6} sx={{ justifySelf: 'center' }}>
            <Text size="sm" color="dimmed" role="rowheader">
                {label}
            </Text>
            {items.map((item, i) => (
                <Fragment key={i}>
                    {i > 0 ? <Divider orientation="vertical" my={10} /> : null}
                    {typeof item.value === 'number' ? (
                        <Tooltip disabled={!item.tooltip} withinPortal label={item.tooltip ?? ''}>
                            <span>
                                <AnchorButton size="sm" text={fmtSvc.formatInt0Dec(item.value)} {...link(item.link)} />
                            </span>
                        </Tooltip>
                    ) : (
                        <Text color="dimmed">&mdash;</Text>
                    )}
                </Fragment>
            ))}
        </Group>
    );
}

function TagStatHeader({ text }: { text: string }) {
    return (
        <Text role="columnheader" size="sm" color="dimmed" align="center">
            {text}
        </Text>
    );
}

const ListContainerEl = styled.div`
    [role='rowheader'] {
        display: none;
    }
    [role='listitem'] {
        > :nth-child(3) {
            justify-self: center;
        }
    }
    &.--list-narrow {
        .list-header {
            display: none;
        }
        [role='listitem'] {
            grid-template-columns: 30px minmax(150px, max-content) 1fr 30px;
            grid-template-rows: repeat(3, 25px);
            grid-template-areas:
                'icon tag stat1 .'
                '. score stat2 delete'
                '. . stat3 .';
            > :nth-child(1) {
                grid-area: icon;
            }
            > :nth-child(2) {
                grid-area: tag;
            }
            > :nth-child(3) {
                grid-area: score;
                justify-self: flex-start;
            }
            > :nth-child(4) {
                grid-area: stat1;
            }
            > :nth-child(5) {
                grid-area: stat2;
            }
            > :nth-child(6) {
                grid-area: stat3;
            }
            > :nth-child(7) {
                grid-area: delete;
            }
            margin: 1rem 0;
        }
        [role='rowheader'] {
            display: block;
        }
        [role='columnheader'] {
            display: none;
        }
        .list-stat {
            justify-self: flex-start;
        }
    }
`;

const ListRow = styled.div<{ clickable?: boolean }>`
    display: grid;
    grid-template-columns: 30px 1fr repeat(4, 120px) 30px;
    border-radius: 6px;
    align-items: center;
    &:hover {
        background: ${(p) => (p.clickable ? p.theme.colors.primary[2] : null)};
    }
    a {
        line-height: 40px;
        padding: 0 4px;
    }
`;
ListRow.defaultProps = { role: 'listitem' };
